import { Link } from "@tanstack/react-router";
import type { BlogListItem } from "../lib/blog.functions";
import { ShareButtons } from "./share-buttons";
import { Breadcrumbs } from "./breadcrumbs";
import { openQuoteModal } from "./quote-modal";
import { heroSrc } from "../lib/blog-image";
import {
  type BlogLocale,
  type BlogLocaleCode,
  SUPPORTED_LOCALES,
} from "../lib/blog-locales";

const SITE = "https://capitalpropressurewashing.com";

const HOME_LABELS: Record<BlogLocaleCode, string> = {
  en: "Home",
  es: "Inicio",
  zh: "首页",
  ur: "صفحہ اول",
  ar: "الرئيسية",
};

function formatDate(value: string, locale: BlogLocale): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return d.toLocaleDateString(locale.dateLocale, {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  } catch {
    return d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    });
  }
}

export function BlogIndexPage({
  articles,
  locale,
}: {
  articles: BlogListItem[];
  locale: BlogLocale;
}) {
  const list = articles;
  const [featured, ...rest] = list;
  const ui = locale.ui;
  const isRtl = locale.dir === "rtl";

  return (
    <main lang={locale.htmlLang} dir={locale.dir}>
      <Breadcrumbs
        items={[{ label: ui.blogTitle }]}
        homeLabel={HOME_LABELS[locale.code]}
      />
      <section className="bg-[var(--brand-deep-blue)] text-white">
        <div className="mx-auto max-w-[1200px] px-4 py-12 md:py-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--brand-red)]">
            {ui.region}
          </p>
          <h1 className="font-[Oswald,sans-serif] text-3xl md:text-5xl font-bold uppercase tracking-wide">
            {ui.blogTitle}
          </h1>
          <p className="mt-3 max-w-2xl text-white/80">{ui.blogIntro}</p>

          {/* Language switcher — links to all other locale blog indexes */}
          <div className="mt-4 flex flex-wrap gap-2">
            {SUPPORTED_LOCALES.filter((l) => l.code !== locale.code).map((l) => (
              <Link
                key={l.code}
                to={l.indexRoute}
                className="rounded-md border border-white/20 px-3 py-1.5 text-sm font-semibold text-white/85 transition-colors hover:bg-white/10 hover:text-white"
              >
                {l.label}
              </Link>
            ))}
          </div>

          <ShareButtons
            url={`${SITE}${locale.indexRoute}`}
            title={`${ui.blogTitle} | Capital Pro Pressure Washing`}
            className="mt-6 [&>span]:text-white/70"
          />
        </div>
      </section>
      <div className="mx-auto max-w-[1200px] px-4 py-12">
        {list.length === 0 ? (
          <p className="text-[var(--brand-charcoal)]/70">{ui.newArticlesSoon}</p>
        ) : (
          <>
            {featured ? (
              <article className="group mb-12 grid overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md md:grid-cols-2">
                <Link
                  to="/blog/$slug"
                  params={{ slug: featured.slug }}
                  className="block aspect-[16/9] w-full overflow-hidden bg-[var(--brand-light-gray)] md:aspect-auto md:h-[420px]"
                >
                  {featured.hero_image_url ? (
                    <img
                      src={heroSrc(featured.hero_image_url, 800)}
                      alt={featured.title}
                      width={800}
                      height={450}
                      fetchPriority="high"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                    />
                  ) : null}
                </Link>
                <div className="flex flex-col justify-center p-6 md:p-10">
                  <span className="inline-flex w-fit rounded-full bg-[var(--brand-red)] px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white">
                    {ui.latest}
                  </span>
                  <time
                  suppressHydrationWarning
                    dateTime={featured.created_at}
                    className="mt-4 text-xs font-semibold uppercase tracking-wide text-[var(--brand-charcoal)]/60"
                  >
                    {formatDate(featured.created_at, locale)}
                  </time>
                  {featured.read_minutes > 0 ? (
                    <span className="mt-1 text-xs font-semibold uppercase tracking-wide text-[var(--brand-charcoal)]/60">
                      {ui.minRead(featured.read_minutes)}
                    </span>
                  ) : null}
                  <h2 className="mt-2 font-[Oswald,sans-serif] text-2xl md:text-3xl font-bold uppercase leading-tight text-[var(--brand-deep-blue)]">
                    <Link to="/blog/$slug" params={{ slug: featured.slug }}>
                      {featured.title}
                    </Link>
                  </h2>
                  {featured.excerpt ? (
                    <p className="mt-4 text-[var(--brand-charcoal)]/80">
                      {featured.excerpt}
                    </p>
                  ) : null}
                  <Link
                    to="/blog/$slug"
                    params={{ slug: featured.slug }}
                    className="mt-6 inline-block w-fit rounded-md bg-[var(--brand-red)] px-5 py-2.5 text-sm font-bold text-white transition-colors hover:opacity-90"
                  >
                    {ui.readMore}
                  </Link>
                </div>
              </article>
            ) : null}

            {rest.length > 0 ? (
              <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {rest.map((a: BlogListItem, i: number) => (
                  <article
                    key={a.slug}
                    className="group flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md"
                  >
                    <Link
                      to="/blog/$slug"
                      params={{ slug: a.slug }}
                      className="block aspect-[16/9] w-full overflow-hidden bg-[var(--brand-light-gray)]"
                    >
                      {a.hero_image_url ? (
                        <img
                          src={heroSrc(a.hero_image_url, 400)}
                          alt={a.title}
                          width={640}
                          height={360}
                          loading={i < 2 ? "eager" : "lazy"}
                          decoding="async"
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                        />
                      ) : null}
                    </Link>
                    <div className="flex flex-1 flex-col p-5">
                      <div
                        className={`flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[var(--brand-charcoal)]/60 ${isRtl ? "flex-row-reverse" : ""}`}
                      >
                        <time suppressHydrationWarning dateTime={a.created_at}>
                          {formatDate(a.created_at, locale)}
                        </time>
                        {a.read_minutes > 0 ? (
                          <>
                            <span aria-hidden="true">·</span>
                            <span>{ui.minRead(a.read_minutes)}</span>
                          </>
                        ) : null}
                      </div>
                      <h2 className="mt-2 font-[Oswald,sans-serif] text-xl font-bold uppercase leading-tight text-[var(--brand-deep-blue)]">
                        <Link to="/blog/$slug" params={{ slug: a.slug }}>
                          {a.title}
                        </Link>
                      </h2>
                      {a.excerpt ? (
                        <p className="mt-3 line-clamp-4 text-sm text-[var(--brand-charcoal)]/80">
                          {a.excerpt}
                        </p>
                      ) : null}
                      <Link
                        to="/blog/$slug"
                        params={{ slug: a.slug }}
                        className="mt-auto pt-4 inline-block text-sm font-bold text-[var(--brand-red)] hover:underline"
                      >
                        {ui.readMore}
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            ) : null}

            {/* CTA */}
            <section className="mt-14 rounded-lg bg-[var(--brand-light-gray)] px-6 py-10 text-center md:px-10">
              <h2 className="font-[Oswald,sans-serif] text-2xl md:text-3xl font-bold uppercase text-[var(--brand-deep-blue)]">
                {ui.readySpotless}
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-[var(--brand-charcoal)]/80">
                {ui.noPressure}
              </p>
              <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <a
                  href="tel:+15189001913"
                  className="w-full rounded-md bg-[var(--brand-deep-blue)] px-6 py-3 font-bold text-white transition-colors hover:opacity-90 sm:w-auto"
                >
                  {ui.callLabel}
                </a>
                <button
                  type="button"
                  onClick={() => openQuoteModal()}
                  className="w-full rounded-md bg-[var(--brand-red)] px-6 py-3 font-bold text-white transition-colors hover:opacity-90 sm:w-auto"
                >
                  {ui.getFreeQuote}
                </button>
              </div>
            </section>
          </>
        )}
      </div>
    </main>
  );
}

export { formatDate as formatBlogDate };
export { HOME_LABELS as BLOG_HOME_LABELS };
